import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import { IAiChoiceOption, TAiUiElement, useEntityHref } from "@ryde/shared";

import {
  ChatChoiceButton,
  ChatChoiceSubmit,
  ChatElementGroup,
  ChatElementLink,
  ChatElementsWrapper,
} from "./bulk-ai-advisor-drawer.styled";

interface IAiChatElementsProps {
  elements?: TAiUiElement[];
  /**
   * Send an option's value as the user's next message. Tapping a button is deliberately
   * identical to typing the text: the write-confirm gate on the agent side keys off an
   * explicit affirmative, and routing clicks through the same path means it needs no
   * special case.
   */
  onSend: (value: string) => void;
  /**
   * False for every message except the newest assistant turn. Older elements stay visible
   * so the transcript still reads correctly, but answering a question from six messages
   * ago would send a confirmation for something no longer pending.
   */
  interactive: boolean;
}

/** wa.me wants digits only; the stored number may carry spaces, dashes or a leading +. */
const whatsappHref = (value: string): string =>
  `https://wa.me/${value.replace(/[^\d]/g, "")}`;

const handoffHref = (channel: string, value: string): string => {
  if (channel === "whatsapp") {
    return whatsappHref(value);
  }
  return channel === "email"
    ? `mailto:${value}`
    : `tel:${value.replace(/\s/g, "")}`;
};

const MultiChoice: FC<{
  options: IAiChoiceOption[];
  interactive: boolean;
  onSend: (value: string) => void;
}> = ({ options, interactive, onSend }) => {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<string[]>([]);

  const toggle = (value: string): void =>
    setPicked(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value],
    );

  return (
    <ChatElementGroup>
      {options.map(option => (
        <ChatChoiceButton
          key={option.value}
          disabled={!interactive}
          $selected={picked.includes(option.value)}
          onClick={() => toggle(option.value)}
        >
          {option.label}
        </ChatChoiceButton>
      ))}
      <ChatChoiceSubmit
        disabled={!interactive || !picked.length}
        onClick={() => onSend(picked.join(", "))}
      >
        {t("aiAdvisor.sendSelection", "Send")}
      </ChatChoiceSubmit>
    </ChatElementGroup>
  );
};

/**
 * Renders the controls attached to one assistant turn.
 *
 * Everything here is additive: the message text is already a complete answer, so an
 * element that cannot be resolved is skipped rather than shown as a dead control. Unknown
 * element types are ignored by design — the runtime ships new ones ahead of the clients.
 *
 * `entityRef` is absent from this list on purpose: those are rendered inline inside the
 * message text (see `markdown-message`), not as separate controls.
 */
export const AiChatElements: FC<IAiChatElementsProps> = ({
  elements,
  onSend,
  interactive,
}) => {
  const entityHref = useEntityHref();
  const controls = (elements ?? []).filter(el => el.type !== "entityRef");
  if (!controls.length) {
    return null;
  }

  return (
    <ChatElementsWrapper>
      {controls.map((element, index) => {
        if (element.type === "choice") {
          return element.multi ? (
            <MultiChoice
              key={element.id}
              options={element.options}
              interactive={interactive}
              onSend={onSend}
            />
          ) : (
            <ChatElementGroup key={element.id}>
              {element.options.map(option => (
                <ChatChoiceButton
                  key={option.value}
                  disabled={!interactive}
                  onClick={() => onSend(option.value)}
                >
                  {option.label}
                </ChatChoiceButton>
              ))}
            </ChatElementGroup>
          );
        }

        if (element.type === "navigate") {
          const navHref = entityHref(element.target.entity, element.target.id);
          if (!navHref) {
            return null;
          }
          return (
            <ChatElementLink
              key={`nav-${index}`}
              href={navHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              {element.label}
            </ChatElementLink>
          );
        }

        if (element.type === "handoff") {
          return (
            <ChatElementLink
              key={`handoff-${index}`}
              href={handoffHref(element.channel, element.value)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {element.label}
            </ChatElementLink>
          );
        }

        return null;
      })}
    </ChatElementsWrapper>
  );
};
